/**
 * CreateAgentsTopBar Component
 *
 * This component serves as the navigation and submission bar for the Create Agent form.
 * It provides:
 * - Back navigation to the agents list
 * - Form submission functionality using React Query mutations
 * - Success/error state management
 * - Visual feedback for user actions
 *
 * The component uses React Query's useMutation hook to handle the API call
 * and manages the form state through props passed from the parent component.
 */

import { faAnglesLeft } from "@fortawesome/free-solid-svg-icons";
import AgentService from "../../../AppSettings/services/AgentsService";
import { useMutation } from "@tanstack/react-query";
import CreateButton from "../../../Components/CreateButton/CreateButton";
import UnifiedTopBar from "@frontend/Components/TopBar/UnifiedTopBar";
import { AddAgentRequest } from "@frontend/Models/Agent/AgentRequestResponseModels";

interface CreateAgentsTopBarProps {
  formData: AddAgentRequest;
  setError: (error: string) => void;
  setFormData: (data: AddAgentRequest) => void;
  setSuccess: (success: string) => void;
}

/**
 * CreateAgentsTopBar Component
 *
 * @param {Object} props - Component props
 * @param {Object} props.formData - The current form data to be submitted
 * @param {Function} props.setError - Function to set error messages
 * @param {Function} props.setFormData - Function to update form data
 * @param {Function} props.setSuccess - Function to set success messages
 */
const CreateAgentsTopBar: React.FC<CreateAgentsTopBarProps> = ({ formData, setError, setFormData, setSuccess }) => {
  /**
   * React Query mutation for handling agent creation
   * Manages the API call lifecycle including success and error states
   */
  const mutation = useMutation({
    mutationFn: () => AgentService.addAgent(formData),
    onSuccess: (data) => {
      // Handle the successful mutation (e.g., show a success message or refetch data)
      console.log("Agent created successfully:", data);
      // Reset form data to initial state
      setFormData({
        FirstName: "",
        LastName: "",
        Company: "",
        Email: "",
        Phone: "",
        Address: "",
        Jurisdiction: ""
      });
      setError("");
      setSuccess("Successfully created new agent!");
      // Clear success message after 5 seconds
      setTimeout(() => setSuccess(""), 5000);
    },
    onError: (error) => {
      // Handle the error (e.g., show an error message)
      console.error("Error creating agent:", error);
      setError(`Error: ${(error as any).response?.data?.error || error.message}`);
    },
  })

  /**
   * Handler for form submission
   * Triggers the mutation with the current form data
   */
  const handleSubmit = () => {
    console.log(formData);
    // Trigger the mutation with data
    mutation.mutate();
  }

  const unifiedConfig = {
    leftElements: [
      {
        type: 'button',
        icon: faAnglesLeft,
        onClick: () => window.history.back(),
        ariaLabel: 'Back to agents list',
      },
      {
        type: 'text',
        text: 'Create an Agent',
        style: { marginLeft: '10px', fontWeight: 'bold' },
      },
    ],
    rightElements: [
      <CreateButton
        onClick={handleSubmit}
        text="Create Agent"
      />
    ],
    // className: 'createMattersTopBar',
    // TODO:  Readd?
  };

  return <UnifiedTopBar {...(unifiedConfig as any)} />;

  // return (
  //   <div className="createMattersTopBar">
  //     {/* Left side: Back navigation */}
  //     <div id="leftSideCreateMattersTopBar">
  //       <Link to="/agents">
  //         <button className="mattersBackButton">
  //           <FontAwesomeIcon icon={faAnglesLeft} className="mattersBackButton" />
  //         </button>
  //       </Link>
  //       <span>Create an Agent</span>
  //     </div>
  //     {/* Right side: Submit button */}
  //     <div id="rightSideCreateMattersTopBar">
  //       <CreateButton
  //         onClick={handleSubmit}
  //         text="Create Agent"
  //       />
  //     </div>
  //   </div>
  // );
};

export default CreateAgentsTopBar;
